import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import {
  checkInToProfile,
  fetchBusinessProfiles,
  getUserEmail,
  isProfileActive,
} from "../../auth/businessProfile";
import type { BusinessProfile } from "../../auth/businessProfile";

export default function AuthCallback() {
  const { completeSignIn } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const hasAuthParams =
      window.location.search.includes("code=") ||
      window.location.search.includes("error=") ||
      window.location.hash.includes("id_token=") ||
      window.location.hash.includes("access_token=");

    if (!hasAuthParams) {
      navigate("/pos-checkin", { replace: true });
      return () => {
        mounted = false;
      };
    }

    completeSignIn()
      .then(async (user) => {
        const returnUrl = (user?.state as string) || "/pos-checkin";
        const email = getUserEmail(user);
        let profiles: BusinessProfile[] = [];
        if (email) {
          try {
            profiles = await fetchBusinessProfiles(email);
          } catch {
            // Non-fatal: the profile selection page retries and shows the error
          }
        }

        // One active profile: check in straight away, like the web panel selection
        if (profiles.length === 1 && isProfileActive(profiles[0])) {
          await checkInToProfile(profiles[0]);
          if (mounted) navigate(returnUrl, { replace: true });
          return;
        }

        if (mounted) {
          navigate(`/account/select-profile?returnUrl=${encodeURIComponent(returnUrl)}`, {
            replace: true,
          });
        }
      })
      .catch((err: Error) => {
        if (!mounted) return;
        const msg = err.message ?? "";
        if (
          msg.toLowerCase().includes("no matching state") ||
          msg.toLowerCase().includes("invalid_grant")
        ) {
          navigate("/account/login", { replace: true });
        } else {
          setError(msg || "Login failed.");
        }
      });

    return () => {
      mounted = false;
    };
  }, [completeSignIn, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <h1 className="text-lg font-semibold text-gray-900 mb-2">
          Completing sign-in...
        </h1>
        <p className="text-sm text-gray-600">
          Please wait while we finish authentication.
        </p>
        {error && <p className="mt-3 text-sm text-red-600">Error: {error}</p>}
      </div>
    </div>
  );
}
